import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import logoIcetex from '../img/ICETEX-TRANSPARENTE.png';
import logoGovCo from '../img/gov-co.png';
import marcaPais from '../img/marcaPais.png';

// Fuente única (Resolución MinTIC 1519 de 2020): Canales de atención y Red de oficinas
// redirigen al portal institucional para no duplicar horarios, direcciones y teléfonos.
const CANALES_URL = 'https://web.icetex.gov.co/atencion-y-servicios-a-la-ciudadania/canales-de-atencion/canales-digitales';
const POLITICAS_URL = 'https://web.icetex.gov.co/web/portal/el-icetex/normatividad/politicas-lineamientos-y-manuales';

const CARD_LINK =
  'text-cobalt hover:text-cobalt-hover hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt focus-visible:ring-offset-1 rounded-sm';

// Redes sociales institucionales de ICETEX (ícono + usuario).
const SOCIALS = [
  {
    label: 'X (antes Twitter)',
    handle: '@ICETEX',
    href: 'https://twitter.com/ICETEX',
    icon: <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />,
  },
  {
    label: 'Facebook',
    handle: 'ICETEX COLOMBIA',
    href: 'https://www.facebook.com/ICETEXCOLOMBIA',
    icon: <path fill="currentColor" d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />,
  },
  {
    label: 'Instagram',
    handle: 'ICETEX_Colombia',
    href: 'https://www.instagram.com/icetex_colombia/',
    icon: (
      <>
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" fill="none" stroke="currentColor" strokeWidth="2" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  {
    label: 'LinkedIn',
    handle: 'ICETEX',
    href: 'https://www.linkedin.com/company/icetex',
    icon: <path fill="currentColor" d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM6 9H2V21h4zM4 2a2 2 0 100 4 2 2 0 000-4z" />,
  },
];

// Enlaces institucionales (solo rutas/URLs existentes).
const FOOTER_LINKS = [
  { label: 'Políticas', href: POLITICAS_URL, external: true },
  { label: 'Red de oficinas', href: CANALES_URL, external: true },
  { label: 'Canales de atención', href: CANALES_URL, external: true },
  { label: 'Términos y condiciones', to: '/terminos-y-condiciones' },
  { label: 'Mapa del sitio', to: '/mapa-del-sitio' },
];

/**
 * Pie de página GOV.CO (Kit UI v9.2) — variante "Sedes electrónicas y Trámites y
 * servicios", Opción A (entidades con 1 a 3 sedes). ICETEX opera con una sede
 * principal, por lo que no se incluyen bloques de sedes adicionales.
 *
 * Estructura del manual:
 *  - Tarjeta blanca sobre fondo claro, con marco decorativo Golden Brown que asoma
 *    por la izquierda y la parte inferior.
 *  - Encabezado: nombre de la entidad (Cobalt) + logo de la autoridad (ICETEX).
 *  - "Sede principal": dirección, horarios, líneas y correos en una sola columna.
 *  - Fila de redes sociales y fila de enlaces institucionales.
 *  - Barra inferior Cobalt #0943B5 con el logo GOV.CO y la Marca País CO.
 *  - Tipografía: Nunito Sans (`font-heading`) en títulos, Verdana en el cuerpo.
 */
const Footer = () => {
  return (
    <footer className="w-full font-verdana text-[#555]">

      {/* Tarjeta de la sede sobre fondo claro */}
      <div className="bg-[#E9EEF8] px-6 lg:px-10 pt-10 pb-9">
        <div className="relative max-w-[1200px] mx-auto">
          {/* Marco decorativo Golden Brown: enmarca la tarjeta por izquierda, derecha
              y abajo (la parte superior queda limpia sobre el fondo claro). */}
          <div
            aria-hidden="true"
            className="absolute top-7 -left-3.5 -right-3.5 -bottom-3.5 rounded-2xl bg-[#E5A33D]"
          />

          <div className="relative bg-white rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-8 md:p-12">

            {/* Encabezado: nombre de la entidad + logo de la autoridad */}
            <div className="flex flex-col-reverse md:flex-row md:items-start md:justify-between gap-6">
              <h2 className="font-heading text-[1.3rem] font-extrabold text-cobalt">ICETEX</h2>
              <img
                src={logoIcetex}
                alt="Logo ICETEX"
                className="max-w-[150px] w-full md:w-auto object-contain self-center md:self-start"
              />
            </div>

            {/* Sede principal — datos en una sola columna */}
            <div className="mt-7 max-w-2xl">
              <h3 className="font-heading text-[1.05rem] font-bold text-cobalt mb-3">Sede principal</h3>
              <div className="space-y-1 text-[0.92rem] leading-[1.7]">
                <p>Dirección: Carrera 3 No. 18-32, Bogotá D.C. - Cundinamarca</p>
                <p>Código postal: 111711</p>
                <p>Horario de atención: Lunes a viernes 7:30 a.m. - 5:30 p.m.</p>
                <p>
                  Teléfono conmutador:{' '}
                  <a href="tel:+576013821670" className={CARD_LINK}>+57 (601) 382-1670</a>
                </p>
                <p>
                  Call Center:{' '}
                  <a href="tel:+573336025656" className={CARD_LINK}>+57 333 602 5656</a>
                </p>
                <p>
                  Línea gratuita:{' '}
                  <a href="tel:018000916821" className={CARD_LINK}>018000-916821</a>
                </p>
                <p>
                  Línea anticorrupción:{' '}
                  <a href="tel:+576013790521" className={CARD_LINK}>+57 (601) 379-0521</a>
                </p>
                <p>
                  Correo institucional:{' '}
                  <a href="mailto:comunicacionesoficiales@icetex.gov.co" className={CARD_LINK}>comunicacionesoficiales@icetex.gov.co</a>
                </p>
                <p>
                  Correo de notificaciones judiciales:{' '}
                  <a href="mailto:notificaciones@icetex.gov.co" className={CARD_LINK}>notificaciones@icetex.gov.co</a>
                </p>
              </div>

              {/* Redes sociales */}
              <ul className="list-none p-0 m-0 mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-x-8 gap-y-3 text-[0.9rem]">
                {SOCIALS.map(({ label, handle, href, icon }) => (
                  <li key={label}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${label}: ${handle} (abre en una nueva pestaña)`}
                      className={`inline-flex items-center gap-2 ${CARD_LINK}`}
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" className="w-[18px] h-[18px] shrink-0">
                        {icon}
                      </svg>
                      <span>{handle}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enlaces institucionales */}
            <ul className="list-none p-0 m-0 mt-10 flex flex-wrap gap-x-8 gap-y-2 text-[0.9rem]">
              {FOOTER_LINKS.map((item) => (
                <li key={item.label}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" className={CARD_LINK}>
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.to} className={CARD_LINK}>
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Barra inferior GOV.CO + Marca País */}
      <div className="w-full bg-cobalt">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-center md:justify-start gap-4">
          <a
            href="https://www.gov.co/home/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Ir al Portal Único del Estado Colombiano GOV.CO (abre en una nueva pestaña)"
            className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-cobalt rounded"
          >
            <img src={logoGovCo} alt="Logo GOV.CO" className="h-7 w-auto object-contain" />
          </a>
          <span aria-hidden="true" className="w-px h-8 bg-white/40" />
          <img src={marcaPais} alt="Marca País Colombia" className="h-10 w-auto object-contain" />
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer);
